const STORAGE_KEY_PREFIX = 'share_verified_';
const PASSWORD_KEY_PREFIX = 'share_password_';
const THIRTY_MINUTES_MS = 30 * 60 * 1000;

/**
 * Interface for share verification state stored in sessionStorage
 */
export interface ShareVerificationState {
  hisUrl: string;
  verifiedAt: number;
  ttl: number;
}

/**
 * Set share verification state in sessionStorage
 * @param hisUrl - Share URL identifier
 * @param password - Optional password (base64 encoded for basic obfuscation)
 */
export function setShareVerified(hisUrl: string, password?: string): void {
  try {
    const state: ShareVerificationState = {
      hisUrl,
      verifiedAt: Date.now(),
      ttl: THIRTY_MINUTES_MS,
    };

    const key = `${STORAGE_KEY_PREFIX}${hisUrl}`;
    sessionStorage.setItem(key, JSON.stringify(state));

    if (password) {
      const passwordKey = `${PASSWORD_KEY_PREFIX}${hisUrl}`;
      // SECURITY: Base64 is obfuscation only, NOT encryption. For production, use proper encryption or token-based auth.
      const encodedPassword = btoa(password);
      sessionStorage.setItem(passwordKey, encodedPassword);
    }
  } catch (error) {
    console.error('Failed to set share verification:', error);
  }
}

/**
 * Check if share is verified and not expired
 * @param hisUrl - Share URL identifier
 * @returns true if verified and not expired, false otherwise
 */
export function isShareVerified(hisUrl: string): boolean {
  try {
    const key = `${STORAGE_KEY_PREFIX}${hisUrl}`;
    const item = sessionStorage.getItem(key);

    if (!item) {
      return false;
    }

    const state: ShareVerificationState = JSON.parse(item);
    const now = Date.now();
    const age = now - state.verifiedAt;

    return age < state.ttl;
  } catch (error) {
    console.error('Failed to check share verification:', error);
    return false;
  }
}

/**
 * Get share password from sessionStorage
 * @param hisUrl - Share URL identifier
 * @returns Base64 encoded password or null
 */
export function getSharePassword(hisUrl: string): string | null {
  try {
    const key = `${PASSWORD_KEY_PREFIX}${hisUrl}`;
    const encodedPassword = sessionStorage.getItem(key);

    if (!encodedPassword) {
      return null;
    }

    return encodedPassword;
  } catch (error) {
    console.error('Failed to get share password:', error);
    return null;
  }
}

/**
 * Clear share verification from sessionStorage
 * @param hisUrl - Optional share URL identifier. If not provided, clears ALL share verifications
 */
export function clearShareVerification(hisUrl?: string): void {
  try {
    if (hisUrl) {
      const verificationKey = `${STORAGE_KEY_PREFIX}${hisUrl}`;
      const passwordKey = `${PASSWORD_KEY_PREFIX}${hisUrl}`;
      sessionStorage.removeItem(verificationKey);
      sessionStorage.removeItem(passwordKey);
    } else {
      const keys = Object.keys(sessionStorage);
      keys.forEach((key) => {
        if (key.startsWith(STORAGE_KEY_PREFIX) || key.startsWith(PASSWORD_KEY_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    console.error('Failed to clear share verification:', error);
  }
}
